import * as storage from '@elab/core/utils/storage';

import { globalTypes } from '../types';
import { reduceAction } from '../helpers';
import userTypes from '../user/types';

import types from './types';

const flashMessages = {
    wishlist: {
        success: false,
        alreadyInList: false,
    },
    shareWishlist: {
        success: false,
        error: false,
    },
    changeEmail: {
        success: false,
        error: false,
    },
};

// flash messages display state management helper
const getFlashMessagesState = (state, { messagesGroupKey, targetMessageKey, value }) => {
    const messagesGroup = state.flashMessages[messagesGroupKey];

    if (!messagesGroup || messagesGroup[targetMessageKey] === undefined) {
        return { ...state };
    }

    const updatedMessagesGroup = {
        ...messagesGroup,
        [targetMessageKey]: value !== undefined ? value : !messagesGroup[targetMessageKey],
    };

    return {
        ...state,
        flashMessages: {
            ...state.flashMessages,
            [messagesGroupKey]: updatedMessagesGroup,
        },
    };
};

const setFlashMessageGroup = (state, { messagesGroupKey, value }) => {
    const messagesGroup = state.flashMessages[messagesGroupKey];

    if (!messagesGroup) {
        return { ...state };
    }

    for (const key in messagesGroup) {
        messagesGroup[key] = value;
    }

    return {
        ...state,
        flashMessages: {
            ...state.flashMessages,
            [messagesGroupKey]: messagesGroup,
        },
    };
};

const updateMessages = (stateMessages = [], message, remove) => {
    if (Array.isArray(message)) {
        return message.reduce(
            (acc, current) => updateMessages(acc, current, remove),
            stateMessages,
        );
    }

    const messageObject =
        message && typeof message !== 'string'
            ? { id: message.id || message.source, ...message }
            : { id: message, text: message };

    const updatedMessages = stateMessages.filter(
        currentMessage => currentMessage.id !== messageObject.id,
    );

    if (!remove) {
        updatedMessages.unshift(messageObject);
    }

    return updatedMessages;
};

export const initialState = () => ({
    globalMessages: [],
    globalMessage: {},
    temporaryMessage: null,
    flashMessages,
    availableLanguages: [],
    language: null,
    country: null,
    preferredLanguage: null,
    preferredCountry: null,
    site: null,
    baseStore: null,
    occBaseUri: null,
    wishlistMode: null,
    useTurbolinks: true,
    languageDialogClosed: false,
    countryDialogClosed: false,
    imagePlaceholderPath: null,
    submitting: false,
    enterClicked: 0,
    lastTimePageLoad: 0,
    messages: [],
    prevPageData: { isEGiftCard: false, url: '' },
    originAction: null,
    currencyCode: null,
    deliveryCountry: storage.get('deliveryCountry') || '',
    configurations: {},
    brCatalogName: null, // Bloomreach catalog name
    globalPageInfo: {},
});

const reducers = (state = initialState(), action) => {
    switch (action.type) {
        case globalTypes.INITIALIZE: {
            const {
                language,
                country,
                site,
                baseStore,
                occBaseUri,
                wishlistMode,
                availableLanguages,
                preferredLanguage,
                preferredCountry,
                useTurbolinks,
                imagePlaceholderPath,
                currencyCode,
                deliveryCountry,
                brCatalogName,
            } = state;
            return {
                ...initialState(),
                ...{
                    language,
                    country,
                    site,
                    baseStore,
                    occBaseUri,
                    wishlistMode,
                    availableLanguages,
                    preferredLanguage,
                    preferredCountry,
                    useTurbolinks,
                    imagePlaceholderPath,
                    currencyCode,
                    deliveryCountry,
                    brCatalogName,
                },
            };
        }
        case types.ADD_GLOBAL_MESSAGE:
            return {
                ...state,
                globalMessages: [...state.globalMessages, action.payload],
            };
        case types.SET_GLOBAL_MESSAGE:
            return reduceAction(state, 'globalMessage', action.payload);
        case userTypes.SET_AUSTRIA:
            return reduceAction(state, 'deliveryCountry', 'AT');
        case userTypes.SET_GERMANY:
            return reduceAction(state, 'deliveryCountry', 'DE');

        case types.SET_DELIVERY_COUNTRY:
            return reduceAction(state, 'deliveryCountry', action.payload);

        case types.SET_TEMPORARY_MESSAGE:
            return reduceAction(state, 'temporaryMessage', action.payload);

        case types.ERASE_TEMPORARY_MESSAGE:
            return reduceAction(state, 'temporaryMessage', null);

        case types.ENTER_CLICKED:
            return { ...state, enterClicked: state.enterClicked + 1 };

        case types.SET_LAST_TIME_PAGE_LOAD:
            return reduceAction(state, 'lastTimePageLoad', action.payload);

        case types.CLOSE_COUNTRY_DIALOG:
            return reduceAction(state, 'countryDialogClosed', action.payload);

        case types.CLOSE_LANGUAGE_DIALOG:
            return reduceAction(state, 'languageDialogClosed', action.payload);

        case types.SET_AVAILABLE_LANGUAGES:
            return reduceAction(state, 'availableLanguages', action.payload);

        case types.SET_PREFERRED_LANGUAGE:
            return reduceAction(state, 'preferredLanguage', action.payload);

        case types.SET_PREFERRED_COUNTRY:
            return reduceAction(state, 'preferredCountry', action.payload);

        case types.SET_LANGUAGE:
            return reduceAction(state, 'language', action.payload);

        case types.SET_COUNTRY:
            return reduceAction(state, 'country', action.payload);

        case types.SET_SITE:
            return reduceAction(state, 'site', action.payload);

        case types.SET_BASE_STORE:
            return reduceAction(state, 'baseStore', action.payload);

        case types.SET_WISHLIST_MODE:
            return reduceAction(state, 'wishlistMode', action.payload);

        case types.SET_OCC_BASE_URI:
            return reduceAction(state, 'occBaseUri', action.payload);

        case types.SET_IMAGE_PLACEHOLDER_PATH:
            return reduceAction(state, 'imagePlaceholderPath', action.payload);

        case types.SET_CURRENCY_CODE:
            return reduceAction(state, 'currencyCode', action.payload);

        case types.DISABLE_TURBOLINKS:
            return reduceAction(state, 'useTurbolinks', false);

        case types.SET_SUBMITTING:
            return reduceAction(state, 'submitting', action.payload);

        case types.SET_NESTED_LEVEL_DATA:
            return {
                ...state,
                nestedLevelData: {
                    ...state.nestedLevelData,
                    ...action.payload,
                },
            };
        case types.TOGGLE_FLASH_MESSAGE:
            return getFlashMessagesState(state, action.payload);
        case types.SET_FLASH_MESSAGE_GROUP:
            return setFlashMessageGroup(state, action.payload);
        case types.ADD_MESSAGE:
            return {
                ...state,
                messages: updateMessages(state.messages, action.payload),
            };
        case types.UPDATE_MESSAGE:
            return {
                ...state,
                messages: state.messages.map(message =>
                    message.id === action.payload.id ? (message = action.payload) : message,
                ),
            };
        case types.REMOVE_MESSAGE:
            return {
                ...state,
                messages: updateMessages(state.messages, action.payload, true),
            };

        case types.REMOVE_ALL_MESSAGES:
            return state.messages.length ? { ...state, messages: [] } : state;

        case types.SET_PREV_PAGE_DATA:
            return reduceAction(state, 'prevPageData', action.payload);

        case types.SET_ORIGIN_ACTION:
            return reduceAction(state, 'originAction', action.payload);
        case types.SET_BR_CATALOG_NAME:
            return { ...state, brCatalogName: action.payload };
        case types.SET_DELIVERY_COUNTRY_ON_LOAD:
            return reduceAction(
                state,
                'deliveryCountry',
                action.payload && action.payload.deliveryCountry,
            );
        case types.SET_STORE_CONFIG:
            return {
                ...state,
                configurations: {
                    ...state.configurations,
                    [action.payload.key]: action.payload.value,
                },
            };

        case types.SET_GLOBAL_PAGE_INFO:
            return {
                ...state,
                globalPageInfo: {
                    current: action.payload.current,
                    previousPage: action.payload.previousPage,
                },
            };

        case types.SET_THRESHOLD_CONFIGURATION:
            return {
                ...state,
                thresholdConfig: action.payload.configurationObject,
            };

        default:
            return state;
    }
};

export default reducers;
